import { useDatabaseContent } from '@databases/database-state';
import { isEmbed, isNativeEmbedEditorPlugin } from '@databases/modes';
import { requestResize } from '@databases/native-embed-editor-plugin';
import usePanelState from '@databases/panel/usePanelState';
import React, { type ReactNode, useEffect } from 'react';

const NATIVE_EMBED_MAX_HEIGHT = 748;
const NATIVE_EMBED_WIDTH_LARGE = 1200;
const NATIVE_EMBED_WIDTH_DEFAULT = 760;
const WIDTH_DEBOUNCE_MS = 200;

const EMBED_MAX_HEIGHT = 700;
const EMBED_PANEL_MIN_HEIGHT = 560;
const EMBED_ENTRY_MIN_HEIGHT = 100;
const EMBED_DEFAULT_MIN_HEIGHT = 400;
const EMBED_EXTRA_PADDING = 30;

// If there is no toolbar then we are rendering entry view.
// 30px is added to compensate for page header margin + 6px buffer
const ENTRY_VIEW_TOOLBAR_FALLBACK = 30;

function calculateNativeEmbedWidth(contentWidth: number): number {
  return contentWidth >= NATIVE_EMBED_WIDTH_LARGE
    ? NATIVE_EMBED_WIDTH_LARGE
    : NATIVE_EMBED_WIDTH_DEFAULT;
}

function getToolbarHeight(): number {
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  if (!toolbarEl) {
    return ENTRY_VIEW_TOOLBAR_FALLBACK;
  }

  const toolbarHeight = toolbarEl.getBoundingClientRect().height;

  // The ViewWrapper for table layout has a -8px margin-top to offset column drag handle
  // padding, which means it visually overlaps the toolbar by 8px. Subtract that overlap
  // so we don't over-report the total height.
  const viewWrapperEl = document.querySelector<HTMLElement>(
    '[data-testid="layout-view--wrapper"]'
  );
  const viewWrapperMarginTop = viewWrapperEl
    ? Math.abs(parseInt(getComputedStyle(viewWrapperEl).marginTop, 10)) || 0
    : 0;

  return toolbarHeight - viewWrapperMarginTop;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function observeNativeEmbedResize(observedElement: HTMLElement): () => void {
  let lastHeight: number | null = null;
  let fixedWidth: number | null = null;
  let widthDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  const observer = new window.ResizeObserver(() => {
    const newHeight = clamp(
      observedElement.offsetHeight + getToolbarHeight(),
      1,
      NATIVE_EMBED_MAX_HEIGHT
    );

    // Debounce the width capture so we lock in the stable post-load width
    // rather than a transient loading-state width.
    if (fixedWidth === null) {
      if (widthDebounceTimer !== null) {
        clearTimeout(widthDebounceTimer);
      }
      widthDebounceTimer = setTimeout(() => {
        fixedWidth = calculateNativeEmbedWidth(observedElement.offsetWidth);
        // Trigger a resize now that we have the correct stable width.
        requestResize(fixedWidth, lastHeight ?? 1);
      }, WIDTH_DEBOUNCE_MS);
    }

    if (lastHeight === newHeight) {
      return;
    }
    lastHeight = newHeight;

    setTimeout(() => requestResize(fixedWidth ?? undefined, newHeight), 0);
  });

  observer.observe(observedElement);

  return () => {
    observer.disconnect();
    if (widthDebounceTimer !== null) {
      clearTimeout(widthDebounceTimer);
    }
  };
}

function observeEmbedResize(
  observedElement: HTMLElement,
  isPanelVisible: () => boolean,
  entryId: string | null
): () => void {
  const minHeight = isPanelVisible()
    ? EMBED_PANEL_MIN_HEIGHT
    : entryId
    ? EMBED_ENTRY_MIN_HEIGHT
    : EMBED_DEFAULT_MIN_HEIGHT;
  let lastHeight: number | null = minHeight;

  const observer = new window.ResizeObserver(() => {
    const newHeight = clamp(
      observedElement.offsetHeight + getToolbarHeight() + EMBED_EXTRA_PADDING,
      minHeight,
      EMBED_MAX_HEIGHT
    );

    if (lastHeight === newHeight) {
      return;
    }
    lastHeight = newHeight;

    setTimeout(() => {
      window.parent.postMessage(
        JSON.stringify({ method: 'resize', height: Math.max(newHeight, 1) }),
        '*'
      );
    }, 0);
  });

  observer.observe(observedElement);
  return () => observer.unobserve(observedElement);
}

const ResizeObserver: React.FC<{
  children?: ReactNode | undefined;
  disableResize?: boolean;
}> = ({ children, disableResize = false }) => {
  const { entryId } = useDatabaseContent();
  const ref = React.useRef<HTMLDivElement>(null);
  const { isPanelVisible } = usePanelState();

  useEffect(() => {
    const observedElement = ref.current;
    if (!observedElement || disableResize) {
      return;
    }

    if (isNativeEmbedEditorPlugin()) {
      return observeNativeEmbedResize(observedElement);
    }

    if (isEmbed()) {
      return observeEmbedResize(observedElement, isPanelVisible, entryId);
    }
  }, [isPanelVisible, entryId, disableResize]);

  return (
    <div
      ref={ref}
      id="resizeObserver"
      style={{
        display: isNativeEmbedEditorPlugin() ? 'inline-block' : 'block',
        width: '100%',
      }}
    >
      {children}
    </div>
  );
};

export default ResizeObserver;
